<script lang="ts">
    import { onMount } from 'svelte';
    import { link, push } from 'svelte-spa-router';
    import { toast } from '../js/toast.js';
    import { auth } from '../js/auth.js';
    import { AuthLayout, AuthPrimaryButton, AuthStatusCallout } from './app';

    let email = '';
    let errors: Record<string, string> = {};
    let submitting = false;
    let linkSent = false;
    let emailEl: HTMLInputElement;

    onMount(() => emailEl?.focus());

    async function submit() {
        if (!validateForm()) return;
        submitting = true;
        try {
            if (await auth.resetPasswordSend(email)) {
                if ($auth.success) {
                    linkSent = true;
                } else {
                    toast.showNotification($auth.success, $auth.message, 'top');
                }
            }
        } finally {
            submitting = false;
        }
    }

    function validateForm() {
        errors = {};
        let valid = true;
        if (!email) { errors.email = 'Email is required'; valid = false; }
        return valid;
    }

    $: if (errors.email && email) errors.email = '';
</script>

{#if linkSent}
    <AuthLayout title="Check your email" subtitle={`If an account exists for ${email}, you'll receive a reset link shortly.`}>
        <AuthStatusCallout tone="success" icon="envelope outline" title="Reset link sent">
            <p>Open the email and follow the link to choose a new password. The link expires after a short time for security.</p>
            <p>Didn't receive it? Check your spam folder, or try again with the same address.</p>
        </AuthStatusCallout>

        <div class="auth-actions-stack">
            <AuthPrimaryButton type="button" on:click={() => { linkSent = false; }}>
                Send again
            </AuthPrimaryButton>
            <button type="button" class="auth-secondary-button" on:click={() => push('/login')}>
                Back to sign in
            </button>
        </div>
    </AuthLayout>
{:else}
    <AuthLayout title="Reset password" subtitle="Enter your email address and we'll send you a reset link.">
        <form class="auth-form" on:submit|preventDefault={submit} novalidate>
            <div class="auth-form-field" class:error={!!errors.email}>
                <label class="auth-form-label" for="email">Email</label>
                <input
                    bind:this={emailEl}
                    id="email"
                    class="auth-form-input"
                    type="email"
                    inputmode="email"
                    autocomplete="email"
                    spellcheck="false"
                    bind:value={email}
                    placeholder="you@company.com"
                    aria-invalid={errors.email ? 'true' : 'false'}
                    aria-describedby={errors.email ? 'email-error' : undefined}
                />
                {#if errors.email}
                    <span id="email-error" class="auth-error-message" role="alert">{errors.email}</span>
                {/if}
            </div>

            <AuthPrimaryButton loading={submitting} loadingLabel="Sending…">
                Send reset link
            </AuthPrimaryButton>
        </form>

        <p slot="footer">
            Changed your mind?
            <a use:link href="/login" class="auth-inline-link">Back to sign in</a>
        </p>
    </AuthLayout>
{/if}
